import { Model, modelRegistry } from './Model';
import { Product } from '.';
import HashGen from '../utilities/HashGen';
import Money from '../utilities/Money';
import { I18n } from '../wrappers/I18n';
export class BeginningInventoryItem extends Model {
    static entity = 'beginning_inventory_items';
    id = null;
    beginning_inventory_id = null;
    date = null;
    product_id = null;
    unit_of_measure = null;
    tax_rate = '0.00';
    quantity = '1.000';
    selling_price = '0.0000';
    selling_price_after_tax = '0.0000';
    purchase_price = null;
    purchase_price_after_tax = null;
    recorded_at = null;
    created_at = null;
    updated_at = null;
    hash = '';
    static fields() {
        return {
            id: this.attr(null),
            beginning_inventory_id: this.attr(null),
            date: this.attr(null),
            product_id: this.attr(null),
            unit_of_measure: this.attr(null),
            tax_rate: this.attr('0.00', value => this.round(value, 2).toFixed(2)),
            quantity: this.attr('1.000', value => this.round(value, 3).toFixed(3)),
            selling_price: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            selling_price_after_tax: this.attr('0.0000', value => this.round(value, 4).toFixed(4)),
            purchase_price: this.attr(null, value => value !== null ? this.round(value, 4).toFixed(4) : null),
            purchase_price_after_tax: this.attr(null, value => value !== null ? this.round(value, 4).toFixed(4) : null),
            recorded_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            hash: this.attr(HashGen.regular()),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    getTaxRate() {
        return parseFloat(this.tax_rate);
    }
    getQuantity() {
        return parseFloat(this.quantity);
    }
    getSellingPrice() {
        return Money.parse(this.selling_price);
    }
    getPurchasePrice() {
        if (!this.purchase_price)
            return null;
        return Money.parse(this.purchase_price);
    }
    getPurchasePriceAfterTax() {
        if (!this.purchase_price_after_tax)
            return null;
        return Money.parse(this.purchase_price_after_tax);
    }
    getPurchaseValueBeforeTax() {
        const purchasePrice = this.getPurchasePrice();
        if (!purchasePrice)
            return null;
        const value = purchasePrice.multiply(this.getQuantity());
        return Money.parse(value.toFormat('0.0000'));
    }
    getPurchaseTax() {
        const purchaseValueBeforeTax = this.getPurchaseValueBeforeTax();
        if (!purchaseValueBeforeTax)
            return null;
        const value = purchaseValueBeforeTax
            .multiply(this.getTaxRate() / 100);
        return Money.parse(value.toFormat('0.0000'));
    }
    getPurchaseValueAfterTax() {
        if (this.purchase_price_after_tax) {
            const priceAfterTax = Money.parse(this.purchase_price_after_tax);
            const value = priceAfterTax.multiply(this.getQuantity());
            return Money.parse(value.toFormat('0.0000'));
        }
        const purchaseValueBeforeTax = this.getPurchaseValueBeforeTax();
        if (!purchaseValueBeforeTax)
            return null;
        const purchaseTax = this.getPurchaseTax();
        if (!purchaseTax)
            return null;
        const value = purchaseValueBeforeTax
            .add(purchaseTax);
        return Money.parse(value.toFormat('0.0000'));
    }
    getSellingPriceAfterTax() {
        return Money.parse(this.selling_price_after_tax);
    }
    getSellingValue() {
        const value = this.getSellingPrice().multiply(this.getQuantity());
        return Money.parse(value.toFormat('0.0000'));
    }
    getTax() {
        const value = this.getSellingValue().multiply(this.getTaxRate() / 100);
        return Money.parse(value.toFormat('0.0000'));
    }
    getSellingValueAfterTax() {
        const value = this.getSellingValue().add(this.getTax());
        return Money.parse(value.toFormat('0.0000'));
    }
    getProduct() {
        return Product.find(this.product_id);
    }
    getUnitOfMeasure() {
        return I18n.t(`products.unit_of_measure.${this.unit_of_measure}`) !== `products.unit_of_measure.${this.unit_of_measure}`
            ? I18n.t(`products.unit_of_measure.${this.unit_of_measure}`)
            : this.unit_of_measure;
    }
    calculatePrices(value, baseField) {
        let sellingPrice = Money.parse(0);
        let sellingPriceAfterTax = Money.parse(0);
        if (baseField === 'selling_price') {
            sellingPrice = Money.parse(this.round(value, 4));
            sellingPriceAfterTax = Money.parse(sellingPrice.toUnit())
                .multiply(1 + this.getTaxRate() / 100);
        }
        else if (baseField === 'selling_price_after_tax') {
            sellingPriceAfterTax = Money.parse(this.round(value, 4));
            sellingPrice = Money.parse(sellingPriceAfterTax.toUnit())
                .divide(1 + this.getTaxRate() / 100);
        }
        return {
            selling_price: sellingPrice.toFormat('0.0000'),
            selling_price_after_tax: sellingPriceAfterTax.toFormat('0.0000'),
        };
    }
    calculatePurchasePrices(value, baseField) {
        let purchasePrice = Money.parse(0);
        let purchasePriceAfterTax = Money.parse(0);
        if (baseField === 'purchase_price') {
            purchasePrice = Money.parse(this.round(value, 4));
            purchasePriceAfterTax = Money.parse(purchasePrice.toUnit())
                .multiply(1 + this.getTaxRate() / 100);
        }
        else if (baseField === 'purchase_price_after_tax') {
            purchasePriceAfterTax = Money.parse(this.round(value, 4));
            purchasePrice = Money.parse(purchasePriceAfterTax.toUnit())
                .divide(1 + this.getTaxRate() / 100);
        }
        return {
            purchase_price: purchasePrice.toFormat('0.0000'),
            purchase_price_after_tax: purchasePriceAfterTax.toFormat('0.0000'),
        };
    }
}
export default BeginningInventoryItem;
modelRegistry.register('BeginningInventoryItem', BeginningInventoryItem);
//# sourceMappingURL=BeginningInventoryItem.js.map